/**
 * HLOS-26 — Wrapper AES-GCM WebCrypto (espelha `hlos/vault/crypto.py`).
 *
 * Port TS literal de `aes_gcm_encrypt()` e `aes_gcm_decrypt()` (Python).
 * Usa `crypto.subtle` (WebCrypto nativo) — sem dependência externa.
 *
 * **Paridade byte-a-byte com Python** (cryptography lib):
 *   - Mesmo formato de output: `nonce (12) || ciphertext || tag (16)`
 *   - Mesmos parâmetros: AES-256-GCM (key = 32 bytes), IV = 12 bytes
 *   - Mesmo comportamento AAD: ciphertext é authenticated mas não encrypted
 *   - Tampering → throw (WebCrypto OperationError; Python InvalidTag)
 *
 * Por que WebCrypto (não js-sha256, noble, etc.):
 *   - CL-07 (Modelo = ativo): 100% nativo no browser, zero dep runtime
 *   - Auditado (NIST FIPS 197 + SP 800-38D), Constant-time em hardware moderno
 *   - Disponível em todos browsers modernos + Node 19+
 *
 * Garantias Constitucionais:
 *   - **CL-07** (Modelo = ativo): cipher roda 100% no device
 *   - **CL-08** (Ética fechada): IV nunca reutilizado (CSPRNG por chamada)
 *   - **CL-09** (Explicabilidade): auth tag GCM validado em cada read
 *
 * AAD (Additional Authenticated Data) — binding opcional:
 *   - Use para associar ciphertext a um `key_id` ou `chunk_id`
 *   - Não é criptografado, mas autenticado (tag mismatch se AAD mudar)
 *   - Exemplo: `aad = concat([keyId, chunkId])`
 *
 * Referência: `docs/architecture/IDENTITY_AND_KEY_MANAGEMENT.md` §3.1.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

/** Tamanho da chave em bytes (AES-256). */
export const AES_KEY_BYTES = 32;

/** Tamanho do IV/nonce em bytes (NIST SP 800-38D recomenda 96 bits). */
export const AES_NONCE_BYTES = 12;

/** Tamanho do auth tag em bytes (GCM = 128 bits). */
export const AES_TAG_BYTES = 16;

/** Tamanho mínimo do blob (nonce + tag, sem ciphertext). */
export const AES_BLOB_MIN_BYTES = AES_NONCE_BYTES + AES_TAG_BYTES;

// ─────────────────────────────────────────────────────────────────────────────
// Erros
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Erro thrown quando a chave tem tamanho incorreto (CL-08).
 * Espelha `ValueError("key must be 32 bytes...")` em Python.
 */
export class InvalidKeyError extends Error {
  constructor(actualBytes: number) {
    super(
      `AES-256-GCM key must be ${AES_KEY_BYTES} bytes (256-bit), got ${actualBytes}`,
    );
    this.name = "InvalidKeyError";
  }
}

/**
 * Erro thrown quando blob é muito curto para conter nonce + tag.
 * Espelha `ValueError("blob too short...")` em Python.
 */
export class InvalidBlobError extends Error {
  constructor(actualBytes: number) {
    super(
      `AES-GCM blob too short: must be ≥ ${AES_BLOB_MIN_BYTES} bytes (nonce+tag), got ${actualBytes}`,
    );
    this.name = "InvalidBlobError";
  }
}

/**
 * Erro thrown quando a autenticação GCM falha (tag mismatch = tampering
 * ou AAD diferente entre encrypt e decrypt).
 *
 * Espelha `cryptography.exceptions.InvalidTag` em Python.
 */
export class AuthenticationFailedError extends Error {
  constructor(message: string = "AES-GCM authentication failed (tag mismatch): blob tampered or AAD mismatch") {
    super(message);
    this.name = "AuthenticationFailedError";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Encrypt
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Criptografa com AES-256-GCM. Espelha `aes_gcm_encrypt()` (Python).
 *
 * @param key - 32 bytes (AES-256). Típico: KEK ou DEK derivada via Argon2id.
 * @param plaintext - Bytes a criptografar (qualquer length).
 * @param aad - Additional Authenticated Data (opcional). NÃO criptografado,
 *               mas incluído no auth tag. Se mudar entre encrypt e decrypt,
 *               `decrypt()` lança `AuthenticationFailedError`.
 *
 * @returns `nonce (12) || ciphertext || tag (16)` — mesmo formato do Python.
 *
 * @throws `InvalidKeyError` se key ≠ 32 bytes (CL-08).
 */
export async function encrypt(
  key: Uint8Array,
  plaintext: Uint8Array,
  aad?: Uint8Array,
): Promise<Uint8Array> {
  if (key.length !== AES_KEY_BYTES) {
    throw new InvalidKeyError(key.length);
  }

  // CL-08: IV gerado via CSPRNG (nunca reutilizado).
  const nonce = crypto.getRandomValues(new Uint8Array(AES_NONCE_BYTES));

  // Importa key como CryptoKey AES-GCM (one-shot usage).
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    { name: "AES-GCM", length: 256 },
    false, // extractable = false (defesa em profundidade)
    ["encrypt"],
  );

  // Parâmetros AAD (opcional).
  const params: AesGcmParams = {
    name: "AES-GCM",
    iv: nonce as BufferSource,
    ...(aad && aad.length > 0 ? { additionalData: aad as BufferSource } : {}),
  };

  // Criptografa (output = ciphertext || tag, 16 bytes de tag no final).
  const ctWithTag = new Uint8Array(
    await crypto.subtle.encrypt(params as unknown as AlgorithmIdentifier, cryptoKey, plaintext as BufferSource),
  );

  // Output: nonce (12) || ctWithTag (ct + 16-byte tag)
  const out = new Uint8Array(AES_NONCE_BYTES + ctWithTag.length);
  out.set(nonce, 0);
  out.set(ctWithTag, AES_NONCE_BYTES);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Decrypt
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Decriptografa com AES-256-GCM. Espelha `aes_gcm_decrypt()` (Python).
 *
 * @param key - 32 bytes (mesma key usada em encrypt).
 * @param blob - `nonce (12) || ciphertext || tag (16)` produzido por encrypt.
 * @param aad - Additional Authenticated Data (mesmo usado em encrypt).
 *              Se diferente, decrypt lança `AuthenticationFailedError`.
 *
 * @returns Plaintext decriptografado.
 *
 * @throws `InvalidKeyError` se key ≠ 32 bytes.
 * @throws `InvalidBlobError` se blob < 28 bytes (sem espaço para nonce+tag).
 * @throws `AuthenticationFailedError` se tag mismatch (tampering ou AAD).
 */
export async function decrypt(
  key: Uint8Array,
  blob: Uint8Array,
  aad?: Uint8Array,
): Promise<Uint8Array> {
  if (key.length !== AES_KEY_BYTES) {
    throw new InvalidKeyError(key.length);
  }
  if (blob.length < AES_BLOB_MIN_BYTES) {
    throw new InvalidBlobError(blob.length);
  }

  // Extrai nonce (12) e ctWithTag.
  const nonce = blob.slice(0, AES_NONCE_BYTES);
  const ctWithTag = blob.slice(AES_NONCE_BYTES);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );

  const params: AesGcmParams = {
    name: "AES-GCM",
    iv: nonce as BufferSource,
    ...(aad && aad.length > 0 ? { additionalData: aad as BufferSource } : {}),
  };

  try {
    const plaintext = new Uint8Array(
      await crypto.subtle.decrypt(
        params as unknown as AlgorithmIdentifier,
        cryptoKey,
        ctWithTag as BufferSource,
      ),
    );
    return plaintext;
  } catch (e) {
    // WebCrypto throws `OperationError` (DOMException) on tag mismatch.
    // Wrap em AuthenticationFailedError para API consistente com Python.
    throw new AuthenticationFailedError(
      `${(e instanceof Error ? e.message : String(e))} (tampering ou AAD mismatch)`,
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Helper: concatena múltiplos Uint8Array. */
export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

/** Type guard para o objeto de parâmetros do WebCrypto AES-GCM. */
interface AesGcmParams {
  readonly name: "AES-GCM";
  readonly iv: BufferSource;
  readonly additionalData?: BufferSource;
  readonly tagLength?: number;
}
